import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLang } from "../lib/i18nContext";
import LocalizedLink from "./LocalizedLink";
import LanguageSelector from "./LanguageSelector";

const LINKS = [
  { to: "/products", key: "products" },
  { to: "/how-we-work", key: "how" },
  { to: "/quality", key: "quality" },
  { to: "/about", key: "about" },
  { to: "/contact", key: "contact" },
];

// Supplied logo artwork, never retyped. Full lockup stays at or above the 180px minimum.
export const Logo = ({ dark = false, className = "w-[180px]" }) => (
  <LocalizedLink to="/" data-testid={dark ? "logo-link-light" : "logo-link"} className="leading-none select-none block shrink-0">
    <img
      src="/stige-logo.webp"
      alt="Stige Lifesciences"
      width={1367}
      height={419}
      className={`${className} h-auto ${dark ? "brightness-0 invert" : ""}`}
    />
  </LocalizedLink>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { localize } = useLang();

  const isActive = (to) => {
    const bare = location.pathname.replace(/^\/(ar|fr|es|pt|vi|id|ms)(?=\/|$)/, "") || "/";
    return bare.startsWith(to);
  };

  return (
    <>
      <header data-testid="main-nav" className="fixed top-0 inset-x-0 z-50 bg-white border-b border-ice">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16 h-nav flex items-center justify-between gap-8">
          <Logo />
          <nav className="hidden xl:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={localize(l.to)}
                data-testid={`nav-link-${l.key}`}
                className={`text-sm font-medium transition-colors duration-300 hover:text-pine ${isActive(l.to) ? "text-pine" : "text-ink"}`}
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <div className="hidden md:block">
              <LanguageSelector />
            </div>
            <LocalizedLink
              to="/contact"
              data-testid="nav-send-requirement-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-ink text-white text-sm font-semibold ps-5 pe-4 py-3 rounded transition-colors duration-300 hover:bg-deep whitespace-nowrap"
            >
              {t("nav.cta")}
              <ArrowUpRight size={15} strokeWidth={2.2} className="rtl:-scale-x-100" />
            </LocalizedLink>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)} aria-label={t("nav.openMenu")} className="xl:hidden p-2 text-ink">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="on-navy fixed inset-0 z-[60] bg-ink text-white flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="h-nav px-6 flex items-center justify-between border-b border-white/10">
              <Logo dark />
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label={t("nav.closeMenu")} className="p-2">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2 overflow-y-auto py-6" aria-label="Mobile">
              {[{ to: "/", key: "home" }, ...LINKS].map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.04, duration: 0.25 }}>
                  <NavLink
                    to={localize(l.to)}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.key}`}
                    className={`font-display text-3xl font-semibold py-2 block transition-colors ${isActive(l.to) && l.to !== "/" ? "text-ice underline underline-offset-8 decoration-2" : "text-white"}`}
                  >
                    {t(`nav.${l.key}`)}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.25 }} className="pt-8 flex flex-col items-start gap-6">
                <LocalizedLink
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-send-requirement-cta"
                  className="inline-flex items-center gap-2 bg-white text-ink font-semibold px-6 py-3 rounded"
                >
                  {t("nav.cta")} <ArrowUpRight size={16} className="rtl:-scale-x-100" />
                </LocalizedLink>
                <div onClick={(e) => e.stopPropagation()} data-testid="mobile-language-selector" className="border-t border-white/10 pt-6 w-full">
                  <LanguageSelector dark dropUp />
                </div>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
